import { ILevelProjectData } from 'src/app/infrastructure/models/level-project-data';
import { isEmptyObject } from 'src/app/infrastructure/functions/is-empty-object.function';
import { domainUserDisplayName, organisationLabel, userDisplayName } from 'src/app/infrastructure/functions/user-name.function';
import { functionMemberListDto, IFunctionMemberListDto } from 'src/assets/common/models/function-member-list-dto.g';
import { domainUserDto } from 'src/assets/common/models/domain-user-dto.g';
import { domainDto } from 'src/assets/common/models/domain-dto.g';
import { logosDto } from 'src/assets/common/models/logos-dto.g';
import { userDto } from 'src/assets/common/models/user-dto.g';
import { functionDto } from 'src/assets/common/models/function-dto.g';
import { imageBoxDto } from 'src/assets/common/models/image-box-dto.g';
import { imageDto } from 'src/assets/common/models/image-dto.g';
import { functionMemberPeriods_map, IFunctionMemberPeriod } from './function-member-period';
import { systemFunction } from 'src/assets/common/enums/system-function.g';
import { momentPassed } from 'src/app/infrastructure/functions/zone-day.function';
import { accessRole, TAccessRole } from 'src/app/infrastructure/models/access-role';

const __emptyGuid = '00000000-0000-0000-0000-000000000000';
const __guidOrNull = (guid: string | null | undefined): string | null => guid && guid !== __emptyGuid ? guid : null;

export const functionMemberListData = {
    requestedDomainUser_guid: 'requestedDomainUser_guid',
    domain_guid: 'domain_guid',
    requestedProjectMember_guid: 'requestedProjectMember_guid',
    project_guid: 'project_guid',

    guid: 'guid',
    nr: 'nr',
    startDate: 'startDate',
    endDate: 'endDate',
    deleted: 'deleted',
    inactive: 'inactive',
    // !inactive én effectiveEndDate niet verstreken — filter-, sorteer- en dim-veld volgens de
    // generieke active-conventie (zelfde sleutel als domainUserListData.active).
    active: 'active',

    domain_name: 'domain_name',
    domain_logo_guid: 'domain_logo_guid',
    domain_photo_guid: 'domain_photo_guid',

    user_guid: 'user_guid',
    user_name: 'user_name',
    user_email: 'user_email',
    // Organisatie van de functionaris (via zijn DomainUser): 'intern' of de contactnaam; null = onbekend.
    organisation_label: 'organisation_label',
    user_photo_guid: 'user_photo_guid',
    // Cache-bust (?v=) voor user_photo_guid — zie domain-user-list-data.ts (profilePhoto_version).
    user_photo_version: 'user_photo_version',
    // Foto van de context van de functie: project-functie → hoofdfoto van het project, domain-functie →
    // hoofdfoto van het domain. Getoond op de FunctionMembers-tab van de DomainUser-pagina.
    context_photo_guid: 'context_photo_guid',
    context_photo_version: 'context_photo_version',

    domainUser_name: 'domainUser_name',
    // Getoonde naam van de persoon: domainUser_name → user_name (userDisplayName, zie
    // user-name.function.ts). Voor weergave/zoeken; domainUser_name is optioneel en vaak leeg.
    displayName: 'displayName',
    domainUser_guid: 'domainUser_guid',
    domainUser_photo_guid: 'domainUser_photo_guid',
    domainUser_photo_version: 'domainUser_photo_version',
    // Zie [[project_functionmember_domainuser_inactive_cascade]]: toegangsperiode van de DomainUser
    // zelf, en de vroegste van endDate/domainUser_endDate — spiegelt functionMemberData.
    domainUser_endDate: 'domainUser_endDate',
    effectiveEndDate: 'effectiveEndDate',

    projectMember_guid: 'projectMember_guid',

    project_name: 'project_name',
    
    function_guid: 'function_guid',
    function_nr: 'function_nr',
    function_name: 'function_name',
    function_displayName: 'function_displayName',
    // DisplayName ?? Name — weergave-label (systeemfuncties hebben een code-veilige Name).
    function_label: 'function_label',
    function_isAdmin: 'function_isAdmin',
    function_isOwner: 'function_isOwner',
    function_appFunction: 'function_appFunction',
    function_DUFunction: 'function_DUFunction',
    function_OMFunction: 'function_OMFunction',
    function_default: 'function_default',
    function_system: 'function_system',
    // Systeemfunctie ProjectManager (de beheerder van het project): eigen rol-icoon i.p.v. administrator.
    function_isProjectManager: 'function_isProjectManager',
    // Rol-niveau van de functie (manager → owner → administrator → gebruiker, zie access-role.ts) —
    // numeriek, zodat lijsten op hiërarchie sorteren; zelfde precedentie als het rol-icoon in de rij.
    function_accessRole: 'function_accessRole',
    // Alle aan/uit-periodes, nieuwste eerst (plan A); de eerdere zijn uitklapbaar in de lijst.
    periods: 'periods',
} as const;

export type TFunctionMemberListData = (typeof functionMemberListData)[keyof typeof functionMemberListData];

export interface IFunctionMemberListData extends ILevelProjectData {
    [functionMemberListData.requestedDomainUser_guid]: string;
    [functionMemberListData.requestedProjectMember_guid]: string;
    [functionMemberListData.domain_guid]: string;
    [functionMemberListData.project_guid]: string;

    [functionMemberListData.guid]: string;
    [functionMemberListData.nr]: number | null;
    [functionMemberListData.startDate]: string | null;
    [functionMemberListData.endDate]: string | null;
    [functionMemberListData.deleted]: boolean;
    [functionMemberListData.inactive]: boolean;
    [functionMemberListData.active]: boolean;

    [functionMemberListData.domain_name]: string | null;
    [functionMemberListData.domain_logo_guid]: string | null;
    [functionMemberListData.domain_photo_guid]: string | null;

    [functionMemberListData.user_guid]: string | null;
    [functionMemberListData.user_name]: string | null;
    [functionMemberListData.user_email]: string | null;
    [functionMemberListData.organisation_label]: string | null;
    [functionMemberListData.user_photo_guid]: string | null;
    [functionMemberListData.user_photo_version]: string | null;
    [functionMemberListData.context_photo_guid]: string | null;
    [functionMemberListData.context_photo_version]: string | null;
    [functionMemberListData.domainUser_guid]: string | null;
    [functionMemberListData.domainUser_name]: string | null;
    [functionMemberListData.displayName]: string;
    [functionMemberListData.domainUser_photo_guid]?: string | null;
    [functionMemberListData.domainUser_photo_version]?: string | null;
    [functionMemberListData.domainUser_endDate]: string | null;
    [functionMemberListData.effectiveEndDate]: string | null;

    [functionMemberListData.project_name]: string | null;
    [functionMemberListData.projectMember_guid]?: string | null;

    [functionMemberListData.function_guid]: string | null;
    [functionMemberListData.function_nr]: number | null;
    [functionMemberListData.function_name]: string | null;
    [functionMemberListData.function_displayName]: string | null;
    [functionMemberListData.function_label]: string | null;
    [functionMemberListData.function_isAdmin]: boolean | null;
    [functionMemberListData.function_isOwner]: boolean | null;
    [functionMemberListData.function_appFunction]: boolean | null;
    [functionMemberListData.function_DUFunction]: boolean | null;
    [functionMemberListData.function_OMFunction]: boolean | null;
    [functionMemberListData.function_default]: boolean | null;
    [functionMemberListData.function_system]: boolean | null;
    [functionMemberListData.function_isProjectManager]: boolean;
    [functionMemberListData.function_accessRole]: TAccessRole;
    [functionMemberListData.periods]: IFunctionMemberPeriod[];
}

export class FunctionMemberListData
    implements IFunctionMemberListData, ILevelProjectData
{
    [functionMemberListData.requestedDomainUser_guid]!: string;
    [functionMemberListData.requestedProjectMember_guid]!: string;
    [functionMemberListData.domain_guid]!: string;
    [functionMemberListData.project_guid]!: string;

    [functionMemberListData.guid]!: string;
    [functionMemberListData.nr]: number | null = null;
    [functionMemberListData.startDate]: string | null = null;
    [functionMemberListData.endDate]: string | null = null;
    [functionMemberListData.deleted]!: boolean;
    [functionMemberListData.inactive]!: boolean;
    [functionMemberListData.active]: boolean = true;

    [functionMemberListData.domain_name]: string | null = null;
    [functionMemberListData.domain_logo_guid]: string | null = null;
    [functionMemberListData.domain_photo_guid]: string | null = null;

    [functionMemberListData.user_guid]: string | null = null;
    [functionMemberListData.user_name]: string | null = null;
    [functionMemberListData.user_email]: string | null = null;
    [functionMemberListData.organisation_label]: string | null = null;
    [functionMemberListData.user_photo_guid]: string | null = null;
    [functionMemberListData.user_photo_version]: string | null = null;
    [functionMemberListData.context_photo_guid]: string | null = null;
    [functionMemberListData.context_photo_version]: string | null = null;
    [functionMemberListData.domainUser_guid]: string | null = null;
    [functionMemberListData.domainUser_name]: string | null = null;
    [functionMemberListData.displayName]: string = '';
    [functionMemberListData.domainUser_photo_guid]?: string | null = null;
    [functionMemberListData.domainUser_photo_version]?: string | null = null;
    [functionMemberListData.domainUser_endDate]: string | null = null;
    [functionMemberListData.effectiveEndDate]: string | null = null;

    [functionMemberListData.project_name]: string | null = null;
    [functionMemberListData.projectMember_guid]?: string | null = null;

    [functionMemberListData.function_guid]: string | null = null;
    [functionMemberListData.function_nr]: number | null = null;
    [functionMemberListData.function_name]: string | null = null;
    [functionMemberListData.function_displayName]: string | null = null;
    [functionMemberListData.function_label]: string | null = null;
    [functionMemberListData.function_isAdmin]: boolean | null = null;
    [functionMemberListData.function_isOwner]: boolean | null = null;
    [functionMemberListData.function_appFunction]: boolean | null = null;
    [functionMemberListData.function_DUFunction]: boolean | null = null;
    [functionMemberListData.function_OMFunction]: boolean | null = null;
    [functionMemberListData.function_default]: boolean | null = null;
    [functionMemberListData.function_system]: boolean | null = null;
    [functionMemberListData.function_isProjectManager]: boolean = false;
    [functionMemberListData.function_accessRole]: TAccessRole = accessRole.user;
    [functionMemberListData.periods]: IFunctionMemberPeriod[] = [];

    private constructor(
        modelListDto?: IFunctionMemberListDto | null
    ) {
        if (!modelListDto || isEmptyObject(modelListDto)) return;
        const __logosDto = modelListDto[functionMemberListDto.domainDto]?.[domainDto.logosDto];
        const __domainDto = modelListDto[functionMemberListDto.domainDto];
        const __userDto = modelListDto[functionMemberListDto.userDto];
        const __domainUserDto = modelListDto[functionMemberListDto.domainUserDto];
        const __functionDto = modelListDto[functionMemberListDto.functionDto];

        // Backend-Guid is niet-nullable: een niet-gemapte requestedDomainUser komt als lege guid binnen —
        // als null behandelen, zodat de route-fallback (routeKeys.dauGuid) in de PageService blijft werken.
        this[functionMemberListData.requestedDomainUser_guid] = __guidOrNull(modelListDto[functionMemberListDto.requestedDomainUser_Guid])!;
        this[functionMemberListData.requestedProjectMember_guid] = __guidOrNull(modelListDto[functionMemberListDto.requestedProjectMember_Guid])!;
        this[functionMemberListData.projectMember_guid] = modelListDto[functionMemberListDto.projectMember_Guid] ?? null;

        this[functionMemberListData.guid] = modelListDto[functionMemberListDto.guid]!;
        this[functionMemberListData.nr] = modelListDto[functionMemberListDto.nr];
        // Kalenderdagen ('yyyy-MM-dd') in de zone van domain/project (StartDay/EndDay; endDate = laatste dag
        // mét toegang) — appDate toont ze als die dag. Verlopen hieronder op de momenten (EndDate).
        this[functionMemberListData.startDate] = modelListDto[functionMemberListDto.startDay];
        this[functionMemberListData.endDate] = modelListDto[functionMemberListDto.endDay];
        this[functionMemberListData.deleted] = modelListDto[functionMemberListDto.deleted]!;
        this[functionMemberListData.inactive] = modelListDto[functionMemberListDto.inactive]!;

        this[functionMemberListData.domain_guid] = modelListDto[functionMemberListDto.domain_Guid]!;
        this[functionMemberListData.project_guid] = modelListDto[functionMemberListDto.project_Guid]!;
        this[functionMemberListData.project_name] = modelListDto[functionMemberListDto.project_Name] ?? null;
        this[functionMemberListData.domain_name] = __domainDto?.[domainDto.name] ?? null;
        this[functionMemberListData.domain_logo_guid] = __logosDto?.[logosDto.logoThumbDto]?.[imageDto.guid] ?? null;
        this[functionMemberListData.domain_photo_guid] = __domainDto?.[domainDto.photoBoxDto]?.[imageBoxDto.photoThumbDto]?.[imageDto.guid] ?? null;

        const __contextBox = modelListDto[functionMemberListDto.project_Guid]
            ? modelListDto[functionMemberListDto.projectPhotoBoxDto]
            : __domainDto?.[domainDto.photoBoxDto];
        this[functionMemberListData.context_photo_guid] = __contextBox?.[imageBoxDto.photoThumbDto]?.[imageDto.guid] ?? null;
        this[functionMemberListData.context_photo_version] = __contextBox?.[imageBoxDto.photoThumbDto]?.[imageDto.modifiedOn]
            ?? __contextBox?.[imageBoxDto.photoDto]?.[imageDto.modifiedOn]
            ?? null;

        this[functionMemberListData.user_guid] = modelListDto[functionMemberListDto.userDto]?.[userDto.guid] ?? null;
        // Getoonde naam van de User; FunctionMember.User is niet altijd geïncludet, DomainUser.User wel.
        this[functionMemberListData.user_name] = userDisplayName(__userDto ?? __domainUserDto?.[domainUserDto.userDto]) || null;
        // FunctionMember.User is niet in elke lijst geïncludet; DomainUser.User wel (StdIncludes) — als terugval.
        this[functionMemberListData.user_email] = modelListDto[functionMemberListDto.userDto]?.[userDto.email]
            ?? __domainUserDto?.[domainUserDto.userDto]?.[userDto.email] ?? null;
        this[functionMemberListData.organisation_label] = organisationLabel(__domainUserDto?.[domainUserDto.affiliation], __domainUserDto?.[domainUserDto.organisation_Name]);
        // Zelfde foto als de DomainUsers-lijst: de profielfoto van de DomainUser, anders de Home-hoofdfoto
        // van de User (ook voor project-functies zonder DomainUser op de FunctionMember).
        const __photoBox = modelListDto[functionMemberListDto.domainUserDto]?.[domainUserDto.profilePhotoDto]
            ?? modelListDto[functionMemberListDto.domainUserDto]?.[domainUserDto.userPhotoDto]
            ?? __userDto?.[userDto.photoBoxDto];
        this[functionMemberListData.user_photo_guid] = __photoBox?.[imageBoxDto.photoThumbDto]?.[imageDto.guid] ?? null;
        this[functionMemberListData.user_photo_version] = __photoBox?.[imageBoxDto.photoThumbDto]?.[imageDto.modifiedOn]
            ?? __photoBox?.[imageBoxDto.photoDto]?.[imageDto.modifiedOn]
            ?? null;

        this[functionMemberListData.domainUser_name] = modelListDto[functionMemberListDto.domainUserDto]?.[domainUserDto.name] ?? null;
        this[functionMemberListData.displayName] = domainUserDisplayName(this[functionMemberListData.domainUser_name], this[functionMemberListData.user_name]);
        this[functionMemberListData.domainUser_guid] = modelListDto[functionMemberListDto.domainUserDto]?.[domainUserDto.guid] ?? null;
        this[functionMemberListData.domainUser_photo_guid] = __domainUserDto?.[domainUserDto.domainPhotoDto]?.[imageBoxDto.photoThumbDto]?.[imageDto.guid] ?? null;
        this[functionMemberListData.domainUser_photo_version] = __domainUserDto?.[domainUserDto.domainPhotoDto]?.[imageBoxDto.photoThumbDto]?.[imageDto.modifiedOn]
            ?? __domainUserDto?.[domainUserDto.domainPhotoDto]?.[imageBoxDto.photoDto]?.[imageDto.modifiedOn]
            ?? null;

        // Zie [[project_functionmember_domainuser_inactive_cascade]]: vroegste van deze
        // FunctionMember's eigen endDate en de toegangsperiode van de DomainUser zelf.
        const __ownEndDay = this[functionMemberListData.endDate];
        this[functionMemberListData.domainUser_endDate] = __domainUserDto?.[domainUserDto.endDay] ?? null;
        const __duEndDay = this[functionMemberListData.domainUser_endDate];
        // Dagstrings (yyyy-MM-dd) zijn als tekst te vergelijken.
        this[functionMemberListData.effectiveEndDate] = !__ownEndDay ? __duEndDay
            : !__duEndDay ? __ownEndDay
            : __ownEndDay <= __duEndDay ? __ownEndDay
            : __duEndDay;
        // Verlopen op de exclusieve eindmomenten (UTC): EndDate <= nu, tijdzonevrij en gelijk aan de backend.
        const __expired = momentPassed(modelListDto[functionMemberListDto.endDate])
            || momentPassed(__domainUserDto?.[domainUserDto.endDate]);
        this[functionMemberListData.active] = !this[functionMemberListData.inactive] && !__expired;

        this[functionMemberListData.function_guid] = modelListDto[functionMemberListDto.functionDto]?.[functionDto.guid] ?? null;
        this[functionMemberListData.function_nr] = modelListDto[functionMemberListDto.functionDto]?.[functionDto.nr] ?? null;
        this[functionMemberListData.function_name] = modelListDto[functionMemberListDto.functionDto]?.[functionDto.name] ?? null;
        this[functionMemberListData.function_isAdmin] = modelListDto[functionMemberListDto.functionDto]?.[functionDto.isAdmin] ?? null;
        this[functionMemberListData.function_isOwner] = modelListDto[functionMemberListDto.functionDto]?.[functionDto.isOwner] ?? null;
        this[functionMemberListData.function_appFunction] = modelListDto[functionMemberListDto.functionDto]?.[functionDto.appFunction] ?? null;
        this[functionMemberListData.function_displayName] = __functionDto?.[functionDto.displayName] ?? null;
        this[functionMemberListData.function_label] = this[functionMemberListData.function_displayName] || this[functionMemberListData.function_name];
        this[functionMemberListData.function_system] = __functionDto?.[functionDto.system] ?? null;
        // Alleen weergave (icoon): System + Name. Een domain-eigen functie is nooit System
        // (FunctionController.Create), dus een eigen functie "ProjectManager" doet zich niet als beheerder voor.
        this[functionMemberListData.function_isProjectManager] = !!this[functionMemberListData.function_system]
            && this[functionMemberListData.function_name]?.toLowerCase() === systemFunction.projectManager.toLowerCase();
        this[functionMemberListData.function_accessRole] = this[functionMemberListData.function_isProjectManager] ? accessRole.manager
            : this[functionMemberListData.function_isOwner] ? accessRole.owner
            : this[functionMemberListData.function_isAdmin] ? accessRole.administrator
            : accessRole.user;
        this[functionMemberListData.periods] = functionMemberPeriods_map(modelListDto[functionMemberListDto.periodsDto]);
    }

    static create(modelListDto?: IFunctionMemberListDto | null): IFunctionMemberListData | null {
        return modelListDto ? new FunctionMemberListData(modelListDto) : null;
    }
}

// Teller naast de uitklapknop van een paneel met de functies van een lid (o.a. Domain › DomainUsers,
// Project › Members): alleen de lopende functies — beëindigde staan wel (gedimd) in het paneel, maar
// tellen niet mee. Geen lopende functies: geen teller, alleen de uitklapknop.
export function functionMembers_activeCountText(list: IFunctionMemberListData[]): string | null {
    const __active = list.filter(fm => fm[functionMemberListData.active]).length;
    return __active > 0 ? String(__active) : null;
}
